import { useState, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Platform,
  Animated,
  KeyboardAvoidingView,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { X, Search, Dumbbell } from 'lucide-react-native';
import { Exercise } from '@/types/database';
import { V1_LIFT_LABELS, V1_LIFT_POOL, normalizeToV1Lift } from '@/lib/lifts/canonical';
import { MUSCLE_GROUPS, lookupExerciseInfo, type MuscleGroup } from '@/constants/exerciseDatabase';

const log = createLogger('MOBILEEXERCISEPICKER');

interface MobileExercisePickerProps {
  visible: boolean;
  onClose: () => void;
  onSelectExercise: (exercise: Exercise) => void;
  exercises: Exercise[];
  recentExercises?: Exercise[];
  initialSelectedBodyPart?: string;
  focusCanonical?: boolean;
}


export default function MobileExercisePicker({
  visible,
  onClose,
  onSelectExercise,
  exercises,
  recentExercises = [],
  initialSelectedBodyPart,
  focusCanonical,
}: MobileExercisePickerProps) {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [movementFilter, setMovementFilter] = useState<'all' | 'push' | 'pull' | 'legs'>('all');
  const [muscleFilter, setMuscleFilter] = useState<MuscleGroup | 'all'>('all');
  const highlightAnim = useRef(new Animated.Value(0)).current;

  // Temporary highlight on canonical section when opened from baseline CTA
  useEffect(() => {
    if (visible && focusCanonical) {
      highlightAnim.setValue(1);
      Animated.timing(highlightAnim, {
        toValue: 0,
        duration: 1500,
        useNativeDriver: false,
      }).start();
    }
  }, [visible, focusCanonical]);

  const normalizeSearch = (text: string) =>
    text.toLowerCase().replace(/[-\s]+/g, '').replace(/s$/, '');

  const filteredExercises = exercises.filter((ex) => {
    const matchesSearch = searchQuery
      ? (() => {
          const normalizedQuery = normalizeSearch(searchQuery);
          const normalizedName = normalizeSearch(ex.name);
          // Exact normalized match
          if (normalizedName.includes(normalizedQuery)) return true;
          // Also check individual words
          const queryWords = searchQuery.toLowerCase().split(/\s+/);
          return queryWords.every(word => ex.name.toLowerCase().includes(word));
        })()
      : true;

    // Muscle group filter from exercise database (with body_part fallback)
    const matchesMuscle = muscleFilter === 'all' ? true : (() => {
      const info = lookupExerciseInfo(ex.name);
      if (info) return info.primaryMuscle === muscleFilter;
      // Fallback: map body_part field to muscle group
      const bp = (ex.body_part || '').toLowerCase();
      const muscleMap: Record<string, string[]> = {
        chest: ['chest', 'pecs', 'pectoral'],
        back: ['back', 'lats', 'upper back', 'lower back', 'traps'],
        shoulders: ['shoulders', 'delts', 'deltoids'],
        legs: ['legs', 'quads', 'hamstrings', 'glutes', 'calves', 'quadriceps', 'lower body'],
        arms: ['arms', 'biceps', 'triceps', 'forearms'],
        core: ['core', 'abs', 'abdominals', 'obliques'],
        full_body: ['full body', 'full_body', 'compound', 'cardio', 'other'],
      };
      const targets = muscleMap[muscleFilter] || [];
      return targets.some(t => bp.includes(t));
    })();

    // Movement type filter (push/pull/legs)
    const matchesMovement = movementFilter === 'all' ? true : (() => {
      const info = lookupExerciseInfo(ex.name);
      const muscle = info?.primaryMuscle || (ex.body_part || '').toLowerCase();
      const pushMuscles = ['chest', 'shoulders', 'arms'];
      const pullMuscles = ['back'];
      const legMuscles = ['legs'];
      // Also check secondary muscles and name patterns
      const name = ex.name.toLowerCase();
      const secondaries = (info?.secondaryMuscles || []).map(s => s.toLowerCase());
      if (movementFilter === 'push') {
        if (pushMuscles.includes(muscle)) {
          // Filter out pull-type arm exercises (bicep curls, etc.)
          if (muscle === 'arms') {
            return !name.includes('curl') && !name.includes('bicep') && !name.includes('hammer') && !name.includes('chin') && !name.includes('pull');
          }
          return true;
        }
        return name.includes('press') || name.includes('push') || name.includes('dip') || name.includes('fly') || name.includes('flye') || (name.includes('raise') && (name.includes('lateral') || name.includes('front')));
      }
      if (movementFilter === 'pull') {
        if (pullMuscles.includes(muscle)) return true;
        if (muscle === 'arms') {
          return name.includes('curl') || name.includes('bicep') || name.includes('hammer') || name.includes('chin') || name.includes('pull');
        }
        return name.includes('row') || name.includes('pull') || name.includes('lat ') || name.includes('deadlift') || name.includes('face pull') || name.includes('reverse fly');
      }
      if (movementFilter === 'legs') {
        if (legMuscles.includes(muscle)) return true;
        return name.includes('squat') || name.includes('lunge') || name.includes('leg') || name.includes('calf') || name.includes('hip') || name.includes('glute');
      }
      return true;
    })();

    return matchesSearch && matchesMuscle && matchesMovement;
  });

  // Show exactly 5 baseline exercises — one per V1 lift, preferring exact label match
  const baselineExerciseIds = new Set<string>();

  // For each V1 lift, find the best matching exercise from the full catalog
  for (const liftKey of V1_LIFT_POOL) {
    const label = V1_LIFT_LABELS[liftKey];
    // Prefer exact name match first
    const exact = exercises.find(e => e.name.toLowerCase() === label.toLowerCase());
    if (exact) {
      baselineExerciseIds.add(exact.id);
      continue;
    }
    // Fallback: first exercise that normalizes to this V1 lift
    const fallback = exercises.find(e => normalizeToV1Lift(e.name) === liftKey);
    if (fallback) baselineExerciseIds.add(fallback.id);
  }

  const isBaselineLift = (exercise: Exercise): boolean => baselineExerciseIds.has(exercise.id);

  // Build display list — override exercise names to canonical V1 labels
  const baselineExercises = filteredExercises
    .filter(isBaselineLift)
    .map(ex => {
      const v1Key = normalizeToV1Lift(ex.name);
      if (v1Key) {
        return { ...ex, name: V1_LIFT_LABELS[v1Key] };
      }
      return ex;
    });
  const allExercises = filteredExercises.filter((ex) => !isBaselineLift(ex));

  log.info({
    selected: initialSelectedBodyPart || 'all',
    results: filteredExercises.length
  });

  const handleSelectExercise = (exercise: Exercise) => {
    onSelectExercise(exercise);
    setSearchQuery('');
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const getExerciseBodyPartLabel = (exercise: Exercise): string => {
    const bodyPart = exercise.body_part?.trim();

    if (bodyPart) {
      const parts = bodyPart.split(/[,/|]/).map(part => {
        const normalized = part.trim();
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
      });

      return parts.length > 1 ? parts.join(' \u2022 ') : parts[0];
    }

    const category = exercise.category?.trim() || '';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
          <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
            Select Exercise
          </Text>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <X size={24} color={theme.colors.text} />
          </TouchableOpacity>
        </View>

        <View style={styles.searchContainer}>
          <View
            style={[
              styles.searchInput,
              {
                backgroundColor: theme.colors.surfaceVariant,
                borderColor: theme.colors.border,
              },
            ]}>
            <Search size={20} color={theme.colors.textSecondary} />
            <TextInput
              style={[styles.searchText, { color: theme.colors.text }]}
              placeholder="Search exercises..."
              placeholderTextColor={theme.colors.textTertiary}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>
        </View>

        {/* Movement type filter (Push/Pull/Legs) */}
        <View style={{ flexDirection: 'row', paddingHorizontal: 16, gap: 8, marginBottom: 8 }}>
          {([
            { id: 'all' as const, label: 'All' },
            { id: 'push' as const, label: '💪 Push' },
            { id: 'pull' as const, label: '🏋️ Pull' },
            { id: 'legs' as const, label: '🦵 Legs' },
          ]).map((m) => {
            const isActive = movementFilter === m.id;
            return (
              <TouchableOpacity
                key={m.id}
                onPress={() => setMovementFilter(m.id)}
                style={{
                  flex: 1,
                  paddingVertical: 8,
                  borderRadius: 2,
                  backgroundColor: isActive ? theme.colors.accent : theme.colors.surfaceVariant,
                  borderWidth: 1,
                  borderColor: isActive ? theme.colors.accent : theme.colors.border,
                  alignItems: 'center',
                }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: isActive ? '#fff' : theme.colors.textSecondary }}>
                  {m.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryScroll}
          contentContainerStyle={styles.categoryScrollContent}>
          {MUSCLE_GROUPS.map((mg) => {
            const isActive = muscleFilter === mg.id;
            return (
              <TouchableOpacity
                key={mg.id}
                onPress={() => setMuscleFilter(mg.id)}
                style={[
                  styles.categoryChip,
                  {
                    backgroundColor: isActive ? theme.colors.accent : theme.colors.surfaceVariant,
                    borderColor: isActive ? theme.colors.accent : theme.colors.border,
                  },
                ]}>
                <Text
                  style={[
                    styles.categoryChipText,
                    { color: isActive ? '#fff' : theme.colors.textSecondary },
                  ]}>
                  {mg.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <ScrollView style={styles.exerciseList} contentContainerStyle={styles.exerciseListContent}>
          {recentExercises.length > 0 && !searchQuery && muscleFilter === 'all' && (
            <>
              <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>
                Recent Exercises
              </Text>
              {recentExercises.map((exercise) => (
                <TouchableOpacity
                  key={`recent-${exercise.id}`}
                  style={[
                    styles.exerciseItem,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.border,
                    },
                  ]}
                  onPress={() => handleSelectExercise(exercise)}>
                  <Dumbbell size={20} color={theme.colors.accent} />
                  <View style={styles.exerciseInfo}>
                    <Text style={[styles.exerciseName, { color: theme.colors.text }]}>
                      {exercise.name}
                    </Text>
                    <Text style={[styles.exerciseCategory, { color: theme.colors.textSecondary }]}>
                      {getExerciseBodyPartLabel(exercise)}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
              <View style={[styles.divider, { backgroundColor: theme.colors.border }]} />
            </>
          )}

          {allExercises.length === 0 ? (
            <View style={styles.emptyState}>
              <Dumbbell size={48} color={theme.colors.textTertiary} />
              <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                No exercises found
              </Text>
              <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
                Try a different search or category
              </Text>
            </View>
          ) : (
            <>
              {baselineExercises.length > 0 && (
              <Animated.View
                style={[
                  styles.canonicalHeader,
                  {
                    backgroundColor: highlightAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['transparent', theme.colors.accent + '22'],
                    }),
                  },
                ]}
              >
                <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>
                  Build your Strength Score
                </Text>
                <Text style={[styles.sectionSubtext, { color: theme.colors.textTertiary }]}>
                  These 5 lifts contribute to your baseline and strength score.
                </Text>
              </Animated.View>
              )}

              {baselineExercises.length > 0 && (
                baselineExercises.map((exercise) => (
                  <TouchableOpacity
                    key={`baseline-${exercise.id}`}
                    style={[
                      styles.exerciseItem,
                      {
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.border,
                      },
                    ]}
                    onPress={() => handleSelectExercise(exercise)}>
                    <Dumbbell size={20} color={theme.colors.accent} />
                    <View style={styles.exerciseInfo}>
                      <Text style={[styles.exerciseName, { color: theme.colors.text }]}>
                        {exercise.name}
                      </Text>
                      <Text style={[styles.exerciseCategory, { color: theme.colors.textSecondary }]}>
                        {getExerciseBodyPartLabel(exercise)}
                      </Text>
                    </View>
                    <Text style={[styles.exerciseScoreTag, { color: theme.colors.accent }]}>Scored</Text>
                  </TouchableOpacity>
                ))
              )}

              {baselineExercises.length > 0 && <View style={[styles.divider, { backgroundColor: theme.colors.border }]} />}
              <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>All exercises</Text>
              <Text style={[styles.sectionSubtext, { color: theme.colors.textTertiary }]}>
                Unscored exercises are still logged to your workout history.
              </Text>

              {allExercises.map((exercise) => (
                <TouchableOpacity
                  key={`all-${exercise.id}`}
                  style={[
                    styles.exerciseItem,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.border,
                    },
                  ]}
                  onPress={() => handleSelectExercise(exercise)}>
                  <Dumbbell size={20} color={theme.colors.accent} />
                  <View style={styles.exerciseInfo}>
                    <Text style={[styles.exerciseName, { color: theme.colors.text }]}>
                      {exercise.name}
                    </Text>
                    <Text style={[styles.exerciseCategory, { color: theme.colors.textSecondary }]}>
                      {getExerciseBodyPartLabel(exercise)}
                    </Text>
                  </View>
                  <Text style={[styles.exerciseScoreTag, { color: theme.colors.textSecondary }]}>
                    Unscored
                  </Text>
                </TouchableOpacity>
              ))}
            </>
          )}
        </ScrollView>
      </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    paddingTop: Platform.OS === 'ios' ? 60 : 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  closeButton: {
    padding: 4,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInput: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 2,
    borderWidth: 1,
    gap: 12,
  },
  searchText: {
    flex: 1,
    fontSize: 16,
  },
  categoryScroll: {
    maxHeight: 50,
  },
  categoryScrollContent: {
    paddingHorizontal: 16,
    gap: 8,
  },
  categoryChip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 2,
    borderWidth: 1,
    marginRight: 8,
  },
  categoryChipText: {
    fontSize: 14,
    fontWeight: '600',
  },
  exerciseList: {
    flex: 1,
  },
  exerciseListContent: {
    padding: 16,
    gap: 12,
  },
  canonicalHeader: {
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginHorizontal: -8,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  sectionSubtext: {
    fontSize: 13,
    marginBottom: 10,
  },
  sectionEmptyText: {
    fontSize: 13,
    marginBottom: 12,
  },
  exerciseItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 2,
    borderWidth: 1,
    gap: 12,
    minHeight: 72,
  },
  exerciseInfo: {
    flex: 1,
    gap: 4,
  },
  exerciseName: {
    fontSize: 16,
    fontWeight: '600',
  },
  exerciseCategory: {
    fontSize: 13,
    textTransform: 'capitalize',
  },
  exerciseScoreTag: {
    fontSize: 12,
    fontWeight: '700',
  },
  divider: {
    height: 1,
    marginVertical: 12,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: 12,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
  },
});
